import React, { useState } from "react";
import { Collapse, Progress, Typography, Row, Col, Tag, Space } from "antd";
import { useIntl } from "react-intl";
import { 
    BarChartOutlined, 
    FunctionOutlined
} from "@ant-design/icons";
import { ILatentElementScore, ILatentElementInput } from "../../services/nestjs-base-api/types";

const { Panel } = Collapse;
const { Text, Title } = Typography;

interface LatentElementsBreakdownProps {
    elements: ILatentElementScore[];
}

const LatentElementsBreakdown: React.FunctionComponent<LatentElementsBreakdownProps> = ({
    elements,
}) => {
    const intl = useIntl();
    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    const getScoreColor = (score: number): string => {
        if (score >= 70) return "#52c41a"; // Green
        if (score >= 40) return "#faad14"; // Orange
        return "#ff4d4f"; // Red
    };

    const getTypeIcon = (type: 'QC' | 'LE') => {
        return type === 'QC' ? <BarChartOutlined /> : <FunctionOutlined />;
    };

    const getTypeLabel = (type: 'QC' | 'LE'): string => {
        return type === 'QC' 
            ? intl.formatMessage({ id: 'brandEquity.latentElement.questionCategory' })
            : intl.formatMessage({ id: 'brandEquity.latentElement.latentElement' });
    };

    const renderInputDetails = (inputs: ILatentElementInput[]) => {
        return (
            <div style={{ marginTop: '16px' }}>
                <Title level={5} style={{ marginBottom: '12px' }}>
                    {intl.formatMessage({ id: 'brandEquity.latentElement.inputBreakdown' })}
                </Title>
                <Row gutter={[16, 16]}>
                    {inputs.map((input, index) => (
                        <Col xs={24} lg={12} key={index}>
                            <div style={{ 
                                padding: '16px',
                                backgroundColor: '#fafafa',
                                borderRadius: '8px',
                                border: '1px solid #f0f0f0'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {input.name}
                                    </Text>
                                    <Tag 
                                        icon={getTypeIcon(input.type)}
                                        color={input.type === 'QC' ? 'blue' : 'purple'}
                                    >
                                        {getTypeLabel(input.type)}
                                    </Tag>
                                </div>
                                
                                <Row gutter={[8, 8]} style={{ fontSize: '13px' }}>
                                    <Col span={24}>
                                        <Text type="secondary">
                                            {intl.formatMessage({ id: 'brandEquity.latentElement.value' })}
                                        </Text>
                                        <div style={{ fontWeight: '600', fontSize: '16px', color: input.value === null ? '#8c8c8c' : undefined }}>
                                            {input.value !== null ? `${input.value.toFixed(1)}%` : 'N/A'}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    const generatePanelHeader = (element: ILatentElementScore) => (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%',
            paddingRight: '24px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Text strong style={{ fontSize: '16px' }}>
                    {element.element}
                </Text>
                <Tag color="default">
                    {element.inputs.length} {intl.formatMessage({ id: 'brandEquity.latentElement.inputs' })}
                </Tag>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Progress
                    type="circle"
                    percent={element.score || 0}
                    strokeColor={getScoreColor(element.score || 0)}
                    size={40}
                    format={(percent) => (
                        <span style={{ 
                            fontSize: '12px', 
                            fontWeight: 'bold',
                            color: getScoreColor(percent || 0)
                        }}>
                            {(percent || 0).toFixed(0)}%
                        </span>
                    )}
                />
            </div>
        </div>
    );

    // Summary statistics
    const averageScore = elements.length > 0 
        ? elements.reduce((sum, el) => sum + (el.score || 0), 0) / elements.length 
        : 0;

    const highPerformingElements = elements.filter(el => (el.score || 0) >= 70).length;
    const lowPerformingElements = elements.filter(el => (el.score || 0) < 40).length;

    return (
        <div>
            {/* Summary Section */}
            <div style={{ 
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '12px'
            }}>
                <Row gutter={[24, 16]}>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                                fontSize: '24px', 
                                fontWeight: 'bold',
                                color: getScoreColor(averageScore || 0)
                            }}>
                                {(averageScore || 0).toFixed(1)}%
                            </div>
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'brandEquity.latentElement.averageScore' })}
                            </Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                {highPerformingElements}
                            </div>
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'brandEquity.latentElement.highPerforming' })}
                            </Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                                {lowPerformingElements}
                            </div>
                            <Text type="secondary">
                                {intl.formatMessage({ id: 'brandEquity.latentElement.needsAttention' })}
                            </Text>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Elements Breakdown */}
            <Collapse
                activeKey={activeKeys}
                onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                size="large"
            >
                {elements.map((element, index) => (
                    <Panel
                        header={generatePanelHeader(element)}
                        key={index.toString()}
                        style={{ marginBottom: '8px' }}
                    >
                        <div style={{ padding: '16px 0' }}>
                            {/* Element Score Details */}
                            <Row gutter={[24, 16]} style={{ marginBottom: '20px' }}>
                                <Col xs={24}>
                                    <div style={{ 
                                        padding: '20px',
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        border: '1px solid #f0f0f0',
                                        textAlign: 'center'
                                    }}>
                                        <Space direction="vertical" size="small">
                                            <Text type="secondary">
                                                {intl.formatMessage({ id: 'brandEquity.latentElement.finalScore' })}
                                            </Text>
                                            <div style={{ 
                                                fontSize: '32px', 
                                                fontWeight: 'bold',
                                                color: getScoreColor(element.score || 0)
                                            }}>
                                                {(element.score || 0).toFixed(1)}%
                                            </div>
                                        </Space>
                                    </div>
                                </Col>
                            </Row>

                            {/* Input Details */}
                            {renderInputDetails(element.inputs)}
                        </div>
                    </Panel>
                ))}
            </Collapse>
        </div>
    );
};

export default LatentElementsBreakdown;